import {
  AppliedFilters,
  Button,
  Container,
  EmptyState,
  Loading,
  PremiumFeature,
  Table,
  Tour,
  baseStep,
} from "@fluidattacks/design";
import type { IButtonProps } from "@fluidattacks/design";
import { useCallback, useMemo, useState } from "react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useTheme } from "styled-components";

import { AddGroupModal } from "./add-group-modal";
import { useOrganizationGroupsQuery } from "./hooks";
import { OrganizationGroupOverview } from "./overview";
import { useAddGroupModalStore } from "./utils";

import { SectionHeader } from "components/section-header";
import { EventBar } from "features/event-bar";
import { useGroupsFilters } from "features/groups/filters";
import { UpgradeFreeTrialModal } from "features/upgrade-free-trial-users";
import { ManagedType } from "gql/graphql";
import { useAuthz, useCalendly, useTour } from "hooks";
import { useTable } from "hooks/use-table";
import type {
  IGroupData,
  IOrganizationGroupsProps,
  ITrialData,
} from "pages/organization/groups/types";
import { getTableColumns } from "pages/organization/groups/utils";

const OrganizationGroups: React.FC<IOrganizationGroupsProps> = ({
  organizationId,
}): JSX.Element => {
  const theme = useTheme();
  const { organizationName } = useParams() as { organizationName: string };
  const { t } = useTranslation();
  const { tours, setCompleted } = useTour();
  const [search, setSearch] = useState("");
  const enableTour = !tours.newGroup;
  const [filteredDataset, setFilteredDataset] = useState<IGroupData[]>([]);
  const {
    handleAddGroupModal,
    handleAddGroupTour,
    isAddGroupModalOpen,
    isAddGroupTourOpen,
  } = useAddGroupModalStore();
  const { closeUpgradeModal, isUpgradeOpen, openUpgradeModal } = useCalendly();

  const { canMutate } = useAuthz();
  const canAddGroup = canMutate("add_group");

  const newGroupFreeTrial = "integrates/plans/group-paid-accounts";
  const {
    eventsLoading,
    groups,
    loading,
    organization,
    refetchGroups,
    rootsLoading,
  } = useOrganizationGroupsQuery(organizationId);
  const openNewGroupModal = useCallback((): void => {
    if (enableTour) {
      handleAddGroupTour();
    }
    if (groups[0]?.managed === ManagedType.Trial) {
      openUpgradeModal();
    } else {
      handleAddGroupModal();
    }
  }, [
    enableTour,
    handleAddGroupModal,
    handleAddGroupTour,
    openUpgradeModal,
    groups,
  ]);

  const closeNewGroupModal = useCallback((): void => {
    if (enableTour) {
      setCompleted("newGroup");
      handleAddGroupTour();
    }
    handleAddGroupModal();
    void refetchGroups();
  }, [
    enableTour,
    handleAddGroupModal,
    handleAddGroupTour,
    refetchGroups,
    setCompleted,
  ]);

  const filteredGroups = useMemo((): IGroupData[] => {
    return groups.filter(
      (group): boolean =>
        group.description.toLowerCase().includes(search.toLowerCase()) ||
        group.name.toLowerCase().includes(search.toLowerCase()),
    );
  }, [groups, search]);

  const { Filters, options, removeFilter } = useGroupsFilters({
    dataset: filteredGroups,
    organizationName,
    setFilteredDataset,
  });

  const tableColumns = getTableColumns(
    organization?.trial as ITrialData | undefined,
    rootsLoading,
    eventsLoading,
  );

  const { table } = useTable({
    columns: tableColumns,
    data: filteredDataset,
    id: "groups-table",
    options: {
      actionsOptions: {
        onSearch: setSearch,
        search,
        searchPlaceholder: t("organization.tabs.groups.search"),
      },
      filtersMode: "client-side",
      loading,
      paginationMode: "client-side",
      rowOptions: { getId: (row): string => row.name },
    },
    total: groups.length,
  });

  const getAddGroupButton = useCallback((): IButtonProps | undefined => {
    if (canAddGroup) {
      return {
        onClick: openNewGroupModal,
        text: t("organization.tabs.groups.empty.button"),
      };
    }

    return undefined;
  }, [canAddGroup, openNewGroupModal, t]);

  if (loading) {
    return (
      <Container
        alignItems={"center"}
        display={"flex"}
        height={"100%"}
        justify={"center"}
      >
        <Loading color={"red"} label={"Loading..."} size={"lg"} />
      </Container>
    );
  }

  return (
    <React.Fragment>
      <SectionHeader header={t("organization.tabs.groups.header")} />
      {groups.length === 0 ? (
        <EmptyState
          confirmButton={getAddGroupButton()}
          description={t("organization.tabs.groups.empty.description")}
          imageSrc={"integrates/empty/groupIcon"}
          title={t("organization.tabs.groups.empty.title")}
        />
      ) : (
        <React.Fragment>
          {organization ? (
            <OrganizationGroupOverview
              coveredAuthors={organization.coveredAuthors}
              coveredRepositories={organization.coveredRepositories}
              missedAuthors={organization.missedAuthors}
              missedRepositories={organization.missedRepositories}
              organizationName={organization.name}
            />
          ) : undefined}
          <Container
            bgColor={theme.palette.gray[50]}
            height={"100%"}
            id={"users"}
            px={1.25}
            py={1.25}
          >
            <EventBar organizationName={organizationName} pb={1.25} />
            <Table table={table}>
              <Table.Options>
                <Table.Controls>
                  <Filters />
                  <Container gap={1.25} id={"actions-container"}>
                    {canAddGroup && (
                      <Button
                        icon={"plus"}
                        id={t("organization.tabs.groups.newGroup.new.id")}
                        onClick={openNewGroupModal}
                        tooltip={t(
                          "organization.tabs.groups.newGroup.new.tooltip",
                        )}
                      >
                        {t("organization.tabs.groups.newGroup.new.text")}
                        {groups[0]?.managed === ManagedType.Trial && (
                          <PremiumFeature />
                        )}
                      </Button>
                    )}
                  </Container>
                </Table.Controls>
              </Table.Options>
              <Table.Caption>
                <AppliedFilters onClose={removeFilter} options={options} />
              </Table.Caption>
              <Table.Content />
              <Table.Footer />
            </Table>
          </Container>
        </React.Fragment>
      )}
      {isAddGroupModalOpen ? (
        <AddGroupModal
          isOpen={isAddGroupModalOpen}
          onClickOpen={handleAddGroupModal}
          onClose={closeNewGroupModal}
          organization={organizationName}
          runTour={enableTour}
        />
      ) : undefined}
      {isAddGroupTourOpen ? (
        <Tour
          run={false}
          steps={[
            {
              ...baseStep,
              content: t("tours.addGroup.addButton"),
              disableBeacon: true,
              hideFooter: true,
              target: "#add-group",
            },
          ]}
        />
      ) : undefined}
      {isUpgradeOpen && (
        <UpgradeFreeTrialModal
          description={t("organization.tabs.groups.newGroup.trial.description")}
          img={newGroupFreeTrial}
          onClose={closeUpgradeModal}
          title={t("organization.tabs.groups.newGroup.trial.title")}
        />
      )}
    </React.Fragment>
  );
};

export { OrganizationGroups };
